import { toValue } from 'vue'

import { getActivePinia } from 'pinia'
import dayjs from 'dayjs'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'

import { useCurrentUserStore } from '@common/current-user-layer/stores/currentUser.store'

import { OldEventColorMap, EventBgColorMap, EventLocation } from './constants'

import { RecurringEndType, type EventSearchResult, type CalendarEventOverrides, type CalendarEvent } from './types/events'

// Initialize plugins
dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(advancedFormat)

/**
 * User's preferred IANA timezone: current account profile when available, else browser guess.
 */
export const getTimezone = (): string => {
  const pinia = getActivePinia()
  if (pinia) {
    const profileTz = useCurrentUserStore(pinia).profile?.timeZone
    if (typeof profileTz === 'string' && profileTz.trim().length > 0) {
      return profileTz.trim()
    }
  }
  return dayjs.tz.guess()
}

export const getTimezoneDisplayWithOffset = () => {
  const tz = getTimezone()
  const offset = dayjs().tz(tz).format('Z')
  return `${tz} (GMT${offset})`
}

export const getTimezoneAbbrWithOffset = () => {
  const tz = getTimezone()
  const offset = dayjs().tz(tz).format('Z')
  return `${tz} (${offset})`
}

/**
 * Get the text and background colors for the event, also converts old event colors to new event colors
 * @param eventColor
 * @returns { textColor: string, backgroundColor: string } - The text and background colors for the event
 */
export const getEventColor = (eventColor: string) => {
  // is old event color?
  let normalizedEventColor = String(eventColor || '').toUpperCase()

  if (Object.keys(OldEventColorMap).includes(normalizedEventColor)) {
    normalizedEventColor = OldEventColorMap[normalizedEventColor as keyof typeof OldEventColorMap]
  }
  return {
    textColor: normalizedEventColor,
    backgroundColor: EventBgColorMap[normalizedEventColor as keyof typeof EventBgColorMap],
  }
}

const getEventTime = (event: EventSearchResult) => {
  const userTimezone = getTimezone()
  const localStart = dayjs.utc(event.startTime).tz(userTimezone)
  const localEnd = event.endTime ? dayjs.utc(event.endTime).tz(userTimezone) : null
  const durationMs = localEnd ? localEnd.diff(localStart) : 0
  return { localStart, localEnd, durationMs }
}

/**
 * Get the target day for a monthly recurring event
 * @param rrule - The rrule string
 * @returns The target day or null if it's not a monthly recurring event with BYMONTHDAY
 * @example RRULE:FREQ=MONTHLY;BYMONTHDAY=15 -> targetDay = 15
 */
const getTargetDay = (rrule: string) => {
  if (rrule.includes('FREQ=MONTHLY') && rrule.includes('BYMONTHDAY=')) {
    // Extract the day of month from RRULE
    const match = rrule.match(/BYMONTHDAY=(\d+)/)
    if (match && match[1]) {
      return parseInt(match[1], 10)
    }
  }
  return null
}

const formatEventTime = (time: dayjs.Dayjs | null) => {
  if (!time) {
    return null
  }
  return time.format('YYYY-MM-DDTHH:mm:ssZ')
}

const getEventWithCommonProps = (event: EventSearchResult, groupIdEvent: string, otherProps: CalendarEventOverrides): CalendarEvent => {
  const { textColor, backgroundColor } = getEventColor(event.eventColor)
  const finalEvent = {
    ...event,
    ...otherProps,
    backgroundColor: backgroundColor,
    borderColor: backgroundColor,
    textColor: textColor,
    groupIdEvent: event.groupId,
    groupId: undefined,
  } as CalendarEvent

  return {
    ...finalEvent,
    // Keep the original event props in the extendedProps, because calendar components will use this to display the event
    extendedProps: {
      ...finalEvent,
    },
  }
}

const getMonthlyRepeatEvent = (event: EventSearchResult, originalEventTime: dayjs.Dayjs, targetDay: number): CalendarEvent => {
  const userTimezone = getTimezone()

  // Calculate the duration of the event in milliseconds
  const eventStart = dayjs.utc(event.startTime).tz(event.timezone)
  const eventEnd = event.endTime ? dayjs.utc(event.endTime).tz(event.timezone) : null
  const durationMs = eventEnd ? eventEnd.diff(eventStart) : 0

  // Get the current year and month from the event instance
  const year = eventStart.year()
  const month = eventStart.month()

  // Create a date with the correct day of month in the event's timezone
  // Note: dayjs uses 0-based months like moment
  const correctDate = dayjs.tz(`${year}-${String(month + 1).padStart(2, '0')}-${String(targetDay).padStart(2, '0')}`, event.timezone)

  // Set the time from the original event
  const correctDateWithTime = correctDate.hour(originalEventTime.hour()).minute(originalEventTime.minute()).second(0).millisecond(0)

  // Now convert to the user's timezone for display
  const localStart = correctDateWithTime.tz(userTimezone)
  const localEnd = durationMs > 0 ? localStart.add(durationMs, 'millisecond') : null

  return getEventWithCommonProps(event, event.groupId, {
    start: formatEventTime(localStart),
    end: formatEventTime(localEnd),
  })
}

const getNonMonthlyRepeatEvent = (event: EventSearchResult, originalEventTime: dayjs.Dayjs) => {
  // For all other recurring events, maintain the original local time in the event's timezone
  // This is how Google Calendar works - recurring events maintain their original local time
  const targetHour = originalEventTime.hour()
  const targetMinute = originalEventTime.minute()
  const userTimezone = getTimezone()
  const eventStart = dayjs.utc(event.startTime).tz(event.timezone)
  const eventEnd = event.endTime ? dayjs.utc(event.endTime).tz(event.timezone) : null
  const durationMs = eventEnd ? eventEnd.diff(eventStart) : 0

  // Get the current occurrence date in the event's timezone
  const currentOccurrenceInEventTz = dayjs.utc(event.startTime).tz(event.timezone)

  // Create the correct time for this occurrence in the event's timezone
  const correctEventTime = currentOccurrenceInEventTz.hour(targetHour).minute(targetMinute).second(0).millisecond(0)

  // Convert to user's timezone for display
  const localStart = correctEventTime.tz(userTimezone)
  const localEnd = durationMs > 0 ? localStart.add(durationMs, 'millisecond') : null

  return getEventWithCommonProps(event, event.groupId, {
    start: formatEventTime(localStart),
    end: formatEventTime(localEnd),
  })
}

const handleRecurringEvent = (event: EventSearchResult) => {
  // Check if this is a monthly event with BYMONTHDAY
  const rrule = event.recurringMeta!.rrule
  // Check if it's a monthly event with BYMONTHDAY
  const targetDay = getTargetDay(rrule)

  // Get the original event time in the event's timezone(in which timezone the event is created)
  const recurringMeta = event.recurringMeta as Record<string, unknown> | undefined
  const originalEventTime = dayjs.utc((recurringMeta?.eventStart as string) || event.startTime).tz(event.timezone)

  // Special handling for monthly events with BYMONTHDAY
  if (targetDay !== null) {
    return getMonthlyRepeatEvent(event, originalEventTime, targetDay)
  } else {
    return getNonMonthlyRepeatEvent(event, originalEventTime)
  }
}

const handleNonRecurringEvent = (event: EventSearchResult) => {
  // Convert time to user's timezone
  const { localStart, localEnd } = getEventTime(event)

  return getEventWithCommonProps(event, event.groupId, {
    start: formatEventTime(localStart),
    end: formatEventTime(localEnd),
  })
}

export const processEvents = (events: any[]): any[] => {
  return events.map(event => {
    try {
      if (event.recurringMeta?.rrule) {
        return handleRecurringEvent(event)
      } else {
        return handleNonRecurringEvent(event)
      }
    } catch (error) {
      console.error('Error processing event:', error, event)
      // Return event with original times if processing fails
      return getEventWithCommonProps(event, event.groupId, {})
    }
  })
}

export function formatEventTimes(startTime: string, endTime: string) {
  const userTimezone = getTimezone()
  // Convert to dayjs objects in user's timezone
  const startMoment = dayjs(startTime).tz(userTimezone)
  const endMoment = dayjs(endTime).tz(userTimezone)

  // Handle case where end time is before start time
  if (endMoment.isBefore(startMoment)) {
    endMoment.date(startMoment.date())
  }

  // Format the date using moment to ensure timezone is respected
  const formattedDate = startMoment.format('Do MMM YYYY')

  // Format times
  const fromTime = startMoment.format('h:mm A')
  const toTime = endMoment.format('h:mm A')

  const timeZoneAbbr = startMoment.format('z')
  return {
    date: formattedDate,
    fromTime: fromTime,
    toTime: toTime,
    timeZone: timeZoneAbbr,
  }
}

export const triggerDownload = (blob: Blob, fileName: string) => {
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', fileName)
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
}

export const exportAttendees = async (response: string, fileName: string) => {
  const blob = await fetch(response).then(res => res.blob())
  triggerDownload(blob, fileName)
}

export const shareOnSocial = (platform: string, url: string, text: string) => {
  const fullText = `${text} ${url}`

  const encodedUrl = encodeURIComponent(url)
  const encodedText = encodeURIComponent(text)
  const encodedFullText = encodeURIComponent(fullText)

  const shareUrls: Record<string, string> = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedText}`,
    linkedin: `https://www.linkedin.com/shareArticle?mini=true&url=${encodedUrl}&title=${encodedText}&summary=${encodedText}`,
    twitter: `https://twitter.com/intent/tweet?text=${encodedFullText}`,
    instagram: `https://www.instagram.com/`, // Instagram doesn't support direct URL sharing
    whatsapp: `https://wa.me/?text=${encodedFullText}`,
    telegram: `https://t.me/share/url?url=${encodedUrl}&text=${encodedText}`,
    reddit: `https://www.reddit.com/submit?url=${encodedUrl}&title=${encodedText}`,
    email: `mailto:?subject=${encodedText}&body=${encodedFullText}`,
  }

  if (shareUrls[platform]) {
    window.open(shareUrls[platform], '_blank', 'noopener,noreferrer')
  }
}

export const roundToNext30Minutes = (date: Date) => {
  const userTimezone = getTimezone()
  // Create a dayjs object in the user's timezone
  // This interprets the current moment in the user's timezone
  const dayjsDate = dayjs(date).tz(userTimezone)
  const minutes = dayjsDate.minute()
  const roundedMinutes = Math.ceil(minutes / 30) * 30
  // Return dayjs object with rounded minutes in the user's timezone
  return dayjsDate.minute(roundedMinutes).second(0).millisecond(0)
}

const getRecurringMeta = (recurringMeta: any, startTime: string, recurringEndType: string) => {
  const frequencyMap = {
    day: 'DAILY',
    week: 'WEEKLY',
    month: 'MONTHLY',
    year: 'YEARLY',
  }
  const dayOfWeekMap = {
    monday: 'MO',
    tuesday: 'TU',
    wednesday: 'WE',
    thursday: 'TH',
    friday: 'FR',
    saturday: 'SA',
    sunday: 'SU',
  }

  const { recurringEndDate, monthlyRepeatType, afterOccurrences, repeatsOn, repeatInterval, repeatOccurence } = recurringMeta
  const until = recurringEndDate ? dayjs(recurringEndDate).endOf('day').utc().format() : null
  // Add simple frequency and interval to the rrule
  let rrule = `FREQ=${frequencyMap[repeatInterval?.toLowerCase() as keyof typeof frequencyMap]};INTERVAL=${repeatOccurence}`
  // If selected weekday, then event can reoccur on specific days of the week, this is handled by the BYDAY parameter
  if (repeatsOn.length > 0 && repeatInterval?.toLowerCase() === 'week') {
    rrule += `;BYDAY=${repeatsOn.map((day: string) => dayOfWeekMap[day as keyof typeof dayOfWeekMap]).join(',')}`
  }

  // If selected monthly, then event can reoccur on same date or same day of the week, this is handled by the BYMONTHDAY parameter
  else if (repeatInterval.toLowerCase() === 'month') {
    const dayOfMonth = dayjs(startTime).date()

    if (monthlyRepeatType === 'monthly_day') {
      rrule += `;BYMONTHDAY=${dayOfMonth}`
    } else if (monthlyRepeatType === 'monthly_weekday') {
      const dayOfWeek = dayjs(startTime).format('dd').toUpperCase()
      const weekOfMonth = Math.ceil(dayjs(startTime).date() / 7)
      rrule += `;BYDAY=${weekOfMonth}${dayOfWeek}`
    }
  }

  // This handles end of recurring event after a certain number of occurrences
  if (recurringEndType === RecurringEndType.AFTER) {
    rrule += `;COUNT=${afterOccurrences}`
  }

  // This handles end of recurring event on a specific date
  if (recurringEndType === RecurringEndType.ON && until) {
    // Remove timezone offset (+00:00 or +0000) before formatting
    const cleanUntil = until
      .replace(/\+\d{2}:?\d{2}/, '') // Remove timezone offset like +00:00 or +0000
      .replace(/[-:]/g, '') // Remove hyphens and colons
      .split('.')[0] // Remove milliseconds if present

    rrule += `;UNTIL=${cleanUntil}${cleanUntil.endsWith('Z') ? '' : 'Z'}`
  }

  return { rrule, until }
}

export function transformEventInput(formObjectRef: any, isEditing: boolean) {
  const formObject = toValue(formObjectRef)

  const {
    title,
    description,
    startTime,
    endTime,
    isRecurring,
    eventLocation,
    eventCoverUrl,
    remindOneDayBefore,
    recurringMeta,
    eventPricing,
  } = formObject

  // Get timezone - use the selected timezone from the dropdown
  const selectedTimezone = formObject.timezone || getTimezone()

  // For new events or when times have changed
  // Create dayjs objects in the selected timezone while preserving the user's intended time
  const eventStartTime = dayjs.tz(dayjs(startTime).format('YYYY-MM-DD HH:mm:ss'), selectedTimezone).utc()
  const eventEndTime = dayjs.tz(dayjs(endTime).format('YYYY-MM-DD HH:mm:ss'), selectedTimezone).utc()

  const originalStartTime = isEditing ? dayjs(formObject.selectedStartTime) : null
  const originalEndTime = isEditing ? dayjs(formObject.selectedEndTime) : null

  let rrule: any = null
  let until: any = null

  //
  if (isRecurring) {
    const { rrule: recurringRrule, until: recurringUntil } = getRecurringMeta(
      recurringMeta,
      eventStartTime.format('YYYY-MM-DD HH:mm:ss'),
      formObject.recurringEndType
    )
    rrule = recurringRrule
    until = recurringUntil
  }

  return {
    title: title,
    description: description,
    startTime: eventStartTime,
    endTime: eventEndTime,
    visibility: {
      type: formObject.visibility.type,
      channelId: formObject.visibility.privateChannel ? formObject.visibility.privateChannel : undefined,
      level: formObject.visibility.level ? parseInt(formObject.visibility.level) : undefined,
      courseId: formObject.visibility.course ? formObject.visibility.course : undefined,
    },
    eventPricing,
    eventColor: formObject.eventColor,
    eventLocation: {
      location: eventLocation.location,
      locationUrl: eventLocation.locationUrl,
      hideEventLocationFromNonAttendees: eventLocation.hideEventLocationFromNonAttendees,
    },
    timezone: selectedTimezone,
    eventCoverUrl: eventCoverUrl,
    remindOneDayBefore: remindOneDayBefore,
    hideAttendees: formObject.hideAttendees,
    status: 'active',
    isFree: formObject.isFree,
    isRecurring: isRecurring,
    recurringMeta: isRecurring
      ? {
          eventStart: eventStartTime,
          eventEnd: until,
          rrule: rrule,
        }
      : undefined,
    isCancelled: false,
    notifications: [],
    ...(isEditing
      ? {
          originalStartTime,
          originalEndTime,
          _id: formObject._id,
          updateCurrentOccurrence: formObject.updateCurrentOccurrence,
          updateCurrentAndAllFutureOccurrences: formObject.updateCurrentAndAllFutureOccurrences,
          updateAllOccurrences: formObject.updateAllOccurrences,
        }
      : {}),
  }
}

function parseRRule(rrule: string): Record<string, string> {
  const rruleObj: Record<string, string> = {}
  const rruleParts = rrule.split(';')

  rruleParts.forEach(part => {
    const [key, value] = part.split('=')
    if (key && value) {
      rruleObj[key] = value
    }
  })

  return rruleObj
}

const getRecurringMetaFromRRule = (recurringMeta: any) => {
  const frequencyMap = {
    DAILY: 'day',
    WEEKLY: 'week',
    MONTHLY: 'month',
    YEARLY: 'year',
  } as const
  const dayOfWeekMap: Record<string, string> = {
    MO: 'monday',
    TU: 'tuesday',
    WE: 'wednesday',
    TH: 'thursday',
    FR: 'friday',
    SA: 'saturday',
    SU: 'sunday',
  } as const

  const rruleObj = parseRRule(recurringMeta.rrule || '')
  const freq = rruleObj.FREQ as keyof typeof frequencyMap
  const recurringEndDate = recurringMeta.eventEnd ? dayjs(recurringMeta.eventEnd) : undefined

  return {
    repeatInterval: frequencyMap[freq] || 'day',
    repeatOccurence: parseInt(rruleObj.INTERVAL, 10) || 1,
    repeatsOn: rruleObj.BYDAY ? rruleObj.BYDAY.split(',').map(day => dayOfWeekMap[day]) : [],
    monthlyRepeatType: rruleObj.BYMONTHDAY ? 'monthly_day' : 'monthly_weekday',
    afterOccurrences: rruleObj.COUNT ? parseInt(rruleObj.COUNT, 10) : undefined,
    recurringEndDate: recurringEndDate ? recurringEndDate.toDate() : undefined,
    isNeverEnds: !rruleObj.COUNT && !rruleObj.UNTIL,
  }
}
/**
 * Converts API event response to form data format for editing
 * @param event - The event object from the API
 * @returns EventFormDataType compatible with CreateEventForm
 */
export const convertEventToFormData = (event: any): any => {
  const recurringMeta = getRecurringMetaFromRRule(event.recurringMeta || '')
  const eventId = event._id || event.eventId
  return {
    selectedStartTime: event.startTime ? dayjs(event.startTime) : null,
    selectedEndTime: event.endTime ? dayjs(event.endTime) : null,
    ...(eventId ? { _id: eventId } : {}),
    title: event.title || '',
    description: event.description || '',
    eventCoverUrl: event.eventCoverUrl || '',
    eventColor: event.eventColor || '#ff5c00',
    timezone: event.timezone || getTimezone(),
    hideAttendees: event.hideAttendees || false,
    remindOneDayBefore: event.remindOneDayBefore || false,
    startTime: event.startTime ? dayjs(event.startTime).tz(event.timezone || getTimezone()) : dayjs().tz(getTimezone()),
    endTime: event.endTime ? dayjs(event.endTime).tz(event.timezone || getTimezone()) : dayjs().tz(getTimezone()),
    isFree: event.isFree ?? true,
    isCancelled: event.isCancelled || false,
    isRecurring: event.isRecurring || false,
    recurringEndType: event.recurringMeta?.eventEnd ? 'on' : event.recurringMeta?.afterOccurrences ? 'after' : 'never',
    recurringMeta: {
      ...recurringMeta,
    },
    notification: {
      notifyAt: event.notification?.notifyAt || '',
      cloudTaskId: event.notification?.cloudTaskId || '',
    },
    status: event.status || 'active',
    eventLocation: {
      location: event.eventLocation?.location || '',
      locationUrl: event.eventLocation?.locationUrl || '',
      hideEventLocationFromNonAttendees: event.eventLocation?.hideEventLocationFromNonAttendees || false,
    },
    recurring: {
      isRecurring: event.isRecurring || false,
      repeatOccurence: event.recurringMeta?.repeatOccurence || 1,
      repeatInterval: event.recurringMeta?.repeatInterval || 'day',
      repeatsOn: event.recurringMeta?.repeatsOn || [],
      recurringEndDate: event.recurringMeta?.recurringEndDate ? new Date(event.recurringMeta.recurringEndDate) : undefined,
      isNeverEnds: event.recurringMeta?.isNeverEnds ? 'never' : undefined,
      monthlyRepeatType: event.recurringMeta?.monthlyRepeatType || 'monthly_day',
      afterOccurrences: event.recurringMeta?.afterOccurrences || 10,
    },
    visibility: {
      type: event.visibility?.type || 'PUBLIC',
      level: event.visibility?.level || '',
      course: event.visibility?.courseId || event.visibility?.course || '',
      privateChannel: event.visibility?.channelId || event.visibility?.privateChannel || '',
    },
    eventPricing: {
      price: event.eventPricing?.price || 0,
      currency: event.eventPricing?.currency || 'USD',
      isTestMode: event.eventPricing?.isTestMode || false,
    },
  }
}

/**
 * Returns a safe http(s) href when `raw` is a navigable web URL, otherwise `null`.
 * Accepts `https://`, `http://`, `//host`, and `www.` prefixes (latter two normalized to https).
 */
export function getHttpOrHttpsHref(raw: string | undefined | null): string | null {
  if (raw == null || typeof raw !== 'string') return null
  const t = raw.trim()
  if (!t) return null

  let candidate = t
  if (/^www\./i.test(t)) {
    candidate = `https://${t}`
  } else if (/^\/\//.test(t)) {
    candidate = `https:${t}`
  }

  if (!/^https?:\/\//i.test(candidate)) {
    return null
  }

  try {
    const url = new URL(candidate)
    if (url.protocol !== 'http:' && url.protocol !== 'https:') {
      return null
    }
    return url.href
  } catch {
    return null
  }
}

/** i18n keys for the location *type* chosen in the create-event form. */
const eventLocationTypeLabelKeys: Record<string, string> = {
  [EventLocation.LIVE_ROOM]: 'events.options.location.liveRoom',
  [EventLocation.ZOOM]: 'events.options.location.zoom',
  [EventLocation.MEET]: 'events.options.location.meet',
  [EventLocation.OTHER]: 'events.options.location.other',
}

/**
 * Label for an event's location *type*. `eventLocation.location` stores the option picked in the
 * form (`golive` / `zoom` / `meet` / `other`), so it must never be rendered raw — that is what
 * printed a bare "other" in the event modal. Free text (legacy events) is returned unchanged.
 */
export function getEventLocationTypeLabel(location: string | undefined | null, t: (key: string) => string): string {
  if (!location) return ''
  const labelKey = eventLocationTypeLabelKeys[location]
  return labelKey ? t(labelKey) : location
}

/**
 * What to show as an event's location: the link or address the author entered, falling back to
 * the label of the chosen location type when that field is empty.
 */
export function getEventLocationDisplayText(
  eventLocation: { locationUrl?: string; location?: string } | undefined | null,
  t: (key: string) => string
): string {
  const enteredLocation = eventLocation?.locationUrl?.trim()
  if (enteredLocation) return enteredLocation
  return getEventLocationTypeLabel(eventLocation?.location, t)
}

/** Prefer `locationUrl`, then `location`, for the same resolution order as copy-to-clipboard. */
export function getEventLocationNavigableHref(
  eventLocation: { locationUrl?: string; location?: string } | undefined | null
): string | null {
  if (!eventLocation) return null
  return getHttpOrHttpsHref(eventLocation.locationUrl) ?? getHttpOrHttpsHref(eventLocation.location)
}
